import { useMemo } from 'react';

export type Num = string | number;

export type Form = {
  loanAmount: Num;
  amortizationMonths: Num;
  interestRate: Num;
  baseRentalIncome: Num;
  recoverables: Num;
  otherIncome: Num;
  paymentsPerYear: Num;
  debtServiceObligation: Num;
  grossIncome: Num;
  vacancyRatePct: Num;
  vacancyRateValue: Num;
  effectiveGrossIncome: Num;

  operatingExpensesPct: Num;
  operatingExpensesValue: Num;
  repairsPct: Num;
  repairsValue: Num;
  mgmtFeePct: Num;
  mgmtFeeValue: Num;
  capexReservePct: Num;
  capexReserveValue: Num;

  expenseSubTotal: Num;

  noi: Num; // Net Operating Income (NOI)
  dscr: Num;

  capRate: Num; // %
  ltv: Num; // ratio like 0.70
  appraisedValue: Num; // $
};

export const n = (v: Num) =>
  typeof v === 'string' ? (v.trim() === '' ? NaN : parseFloat(v)) : Number(v);

export const fmtMoney0 = (x: number) =>
  Number.isFinite(x) ? x.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '0';

export const fmt2 = (x: number) => (Number.isFinite(x) ? x.toFixed(2) : '');

export const makeInitial = (loanAmount: number, paymentsPerYear: number): Form => ({
  loanAmount,
  paymentsPerYear,

  amortizationMonths: '',
  interestRate: '',

  baseRentalIncome: '',
  recoverables: '',
  otherIncome: '',

  debtServiceObligation: '',

  grossIncome: '',
  vacancyRatePct: 5,
  vacancyRateValue: '',
  effectiveGrossIncome: '',

  operatingExpensesPct: 14,
  operatingExpensesValue: '',
  repairsPct: 4,
  repairsValue: '',
  mgmtFeePct: 5,
  mgmtFeeValue: '',
  capexReservePct: 1,
  capexReserveValue: '',

  expenseSubTotal: '',

  noi: '',
  dscr: '',

  capRate: 6,
  ltv: '',
  appraisedValue: '',
});

export type Row = Record<string, string | number>;

export function useValuationCalcs(form: Form) {
  // Debt Service Obligation = -PMT(rate/ppy, nper=months, pv=loan, 0, 0) * ppy
  const debtServiceObligation = useMemo<Num>(() => {
    const loan = n(form.loanAmount);
    const ppy = Math.max(1, Math.round(n(form.paymentsPerYear)));
    const months = Math.max(1, Math.round(n(form.amortizationMonths)));
    const iraw = form.interestRate;

    if (isNaN(loan) || isNaN(ppy) || isNaN(months) || iraw === '') return '';

    const aprPct = n(iraw);
    if (isNaN(aprPct)) return '';

    const r = aprPct / 100 / ppy;
    let pmtPerPeriod: number;

    if (r === 0) {
      pmtPerPeriod = loan / months;
    } else {
      const pow = Math.pow(1 + r, months);
      pmtPerPeriod = loan * ((r * pow) / (pow - 1));
    }

    return Math.round(pmtPerPeriod * ppy);
  }, [form.loanAmount, form.paymentsPerYear, form.amortizationMonths, form.interestRate]);

  // Gross Income
  const grossIncome = useMemo<Num>(() => {
    const bri = n(form.baseRentalIncome);
    const rec = n(form.recoverables);
    const oth = n(form.otherIncome);
    if (isNaN(bri) && isNaN(rec) && isNaN(oth)) return '';
    return Math.round((isNaN(bri) ? 0 : bri) + (isNaN(rec) ? 0 : rec) + (isNaN(oth) ? 0 : oth));
  }, [form.baseRentalIncome, form.recoverables, form.otherIncome]);

  // Vacancy value
  const vacancyValue = useMemo<Num>(() => {
    const gi = n(grossIncome);
    const vr = n(form.vacancyRatePct);
    if (isNaN(gi) || isNaN(vr)) return '';
    return Math.round(gi * (vr / 100));
  }, [grossIncome, form.vacancyRatePct]);

  // EGI
  const egi = useMemo<Num>(() => {
    const gi = n(grossIncome);
    const vv = n(vacancyValue);
    if (isNaN(gi) || isNaN(vv)) return '';
    return Math.round(gi - vv);
  }, [grossIncome, vacancyValue]);

  // EGI right label string
  const egiRightLabel = useMemo(() => {
    const egiNum = n(egi);
    const vr = n(form.vacancyRatePct);
    const egiPct = isNaN(vr) ? '' : `${(100 - vr).toFixed(vr % 1 ? 1 : 0)}%`;
    return isNaN(egiNum) ? '$0' : `$${fmtMoney0(egiNum)}${egiPct ? ` (${egiPct})` : ''}`;
  }, [egi, form.vacancyRatePct]);

  // Expense values (pct * Base Rental Income)
  const baseRent = useMemo(() => n(form.baseRentalIncome), [form.baseRentalIncome]);

  const operatingExpensesValue = useMemo<Num>(() => {
    const pct = n(form.operatingExpensesPct);
    if (isNaN(baseRent) || isNaN(pct)) return '';
    return Math.round(baseRent * (pct / 100));
  }, [baseRent, form.operatingExpensesPct]);

  const repairsValue = useMemo<Num>(() => {
    const pct = n(form.repairsPct);
    if (isNaN(baseRent) || isNaN(pct)) return '';
    return Math.round(baseRent * (pct / 100));
  }, [baseRent, form.repairsPct]);

  const mgmtFeeValue = useMemo<Num>(() => {
    const pct = n(form.mgmtFeePct);
    if (isNaN(baseRent) || isNaN(pct)) return '';
    return Math.round(baseRent * (pct / 100));
  }, [baseRent, form.mgmtFeePct]);

  const capexReserveValue = useMemo<Num>(() => {
    const pct = n(form.capexReservePct);
    if (isNaN(baseRent) || isNaN(pct)) return '';
    return Math.round(baseRent * (pct / 100));
  }, [baseRent, form.capexReservePct]);

  // Expense totals
  const expensePctTotal = useMemo<Num>(() => {
    const a = n(form.operatingExpensesPct);
    const b = n(form.repairsPct);
    const c = n(form.mgmtFeePct);
    const d = n(form.capexReservePct);
    if ([a, b, c, d].every((x) => isNaN(x))) return '';
    const sum = (isNaN(a) ? 0 : a) + (isNaN(b) ? 0 : b) + (isNaN(c) ? 0 : c) + (isNaN(d) ? 0 : d);
    return Number.isInteger(sum) ? sum : parseFloat(sum.toFixed(1));
  }, [form.operatingExpensesPct, form.repairsPct, form.mgmtFeePct, form.capexReservePct]);

  const expenseValueTotal = useMemo<Num>(() => {
    const a = n(operatingExpensesValue);
    const b = n(repairsValue);
    const c = n(mgmtFeeValue);
    const d = n(capexReserveValue);
    if ([a, b, c, d].every((x) => isNaN(x))) return '';
    return Math.round(
      (isNaN(a) ? 0 : a) + (isNaN(b) ? 0 : b) + (isNaN(c) ? 0 : c) + (isNaN(d) ? 0 : d),
    );
  }, [operatingExpensesValue, repairsValue, mgmtFeeValue, capexReserveValue]);

  // NOI
  const noi = useMemo<Num>(() => {
    const egiNum = n(egi);
    const exp = n(expenseValueTotal);
    if (isNaN(egiNum) || isNaN(exp)) return '';
    return Math.round(egiNum - exp);
  }, [egi, expenseValueTotal]);

  // DSCR
  const dscr = useMemo<Num>(() => {
    const noiNum = n(noi);
    const dso = n(debtServiceObligation);
    if (isNaN(noiNum) || isNaN(dso) || dso === 0) return '';
    return parseFloat((noiNum / dso).toFixed(2));
  }, [noi, debtServiceObligation]);

  // Appraised value = NOI / CapRate
  const appraisedValue = useMemo<Num>(() => {
    const noiNum = n(noi);
    const cr = n(form.capRate);
    if (isNaN(noiNum) || isNaN(cr) || cr === 0) return '';
    return Math.round(noiNum / (cr / 100));
  }, [noi, form.capRate]);

  // LTV
  const ltv = useMemo<Num>(() => {
    const loan = n(form.loanAmount);
    const val = n(appraisedValue);
    if (isNaN(loan) || isNaN(val) || val === 0) return '';
    return parseFloat((loan / val).toFixed(2));
  }, [form.loanAmount, appraisedValue]);

  // ---------- Sensitivity tables
  // Cap Rate rows (6.0 to 10.0 step 0.5)
  const capRateRows = useMemo<Row[]>(() => {
    const loan = n(form.loanAmount);
    const noiNum = n(noi);
    if (isNaN(noiNum) || noiNum === 0) {
      return Array.from({ length: 9 }, (_, i) => ({
        cap: `${(6 + i * 0.5).toFixed(1)}%`,
        ltv: '',
      }));
    }
    const rows: Row[] = [];
    for (let cap = 6; cap <= 10.00001; cap += 0.5) {
      const appVal = noiNum / (cap / 100);
      const ltvVal = loan / appVal;
      rows.push({
        cap: `${cap.toFixed(1)}%`,
        ltv: Number.isFinite(ltvVal) ? fmt2(ltvVal) : '',
      });
    }
    return rows;
  }, [form.loanAmount, noi]);

  // Vacancy rows (5.0 to 9.0 step 0.5)
  // NOI = GI * (1 - vac%) - ExpenseValueTotal
  // LTV = Loan / (NOI / CapRate(current))
  const vacancyRows = useMemo<Row[]>(() => {
    const gi = n(grossIncome);
    const expTotal = n(expenseValueTotal);
    const cap = n(form.capRate);
    const loan = n(form.loanAmount);
    const rows: Row[] = [];
    for (let v = 5; v <= 9.00001; v += 0.5) {
      let noiStr = '',
        ltvStr = '';
      if (!isNaN(gi) && !isNaN(expTotal)) {
        const noiV = Math.round(gi * (1 - v / 100) - expTotal);
        noiStr = `$${fmtMoney0(noiV)}`;
        if (!isNaN(loan) && !isNaN(cap) && cap !== 0) {
          const appVal = noiV / (cap / 100);
          const ltvVal = loan / appVal;
          ltvStr = Number.isFinite(ltvVal) ? fmt2(ltvVal) : '';
        }
      }
      rows.push({ vacancy: `${v.toFixed(1)}%`, noi: noiStr, ltv: ltvStr });
    }
    return rows;
  }, [grossIncome, expenseValueTotal, form.capRate, form.loanAmount]);

  // Expenses rows (24.0 to 28.0 step 0.5)
  // NOI = (B+R+O) - currentVacancyValue - (BaseRent * exp%)
  const expensesRows = useMemo<Row[]>(() => {
    const base = n(form.baseRentalIncome);
    const rec = n(form.recoverables);
    const oth = n(form.otherIncome);
    const gi = (isNaN(base) ? 0 : base) + (isNaN(rec) ? 0 : rec) + (isNaN(oth) ? 0 : oth);
    const currentVacPct = n(form.vacancyRatePct);
    const vacVal = !isNaN(gi) && !isNaN(currentVacPct) ? gi * (currentVacPct / 100) : NaN;

    const loan = n(form.loanAmount);
    const cap = n(form.capRate);

    const rows: Row[] = [];
    for (let p = 24; p <= 28.00001; p += 0.5) {
      let noiStr = '',
        ltvStr = '';
      if (!isNaN(base) && !isNaN(gi) && !isNaN(vacVal)) {
        const expVal = base * (p / 100);
        const noiP = Math.round(gi - vacVal - expVal);
        noiStr = `$${fmtMoney0(noiP)}`;
        if (!isNaN(loan) && !isNaN(cap) && cap !== 0) {
          const appVal = noiP / (cap / 100);
          const ltvVal = loan / appVal;
          ltvStr = Number.isFinite(ltvVal) ? fmt2(ltvVal) : '';
        }
      }
      rows.push({ pct: `${p.toFixed(1)}%`, noi: noiStr, ltv: ltvStr });
    }
    return rows;
  }, [
    form.baseRentalIncome,
    form.recoverables,
    form.otherIncome,
    form.vacancyRatePct,
    form.loanAmount,
    form.capRate,
  ]);

  return {
    // primitives
    debtServiceObligation,
    grossIncome,
    vacancyValue,
    egi,
    egiRightLabel,

    operatingExpensesValue,
    repairsValue,
    mgmtFeeValue,
    capexReserveValue,

    expensePctTotal,
    expenseValueTotal,

    noi,
    dscr,

    appraisedValue,
    ltv,

    // tables
    capRateRows,
    vacancyRows,
    expensesRows,
  };
}
